import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getFeaturePropertyRows,
  getProductPropertyAssociationRows,
  updateFeaturePropertyAssociation,
} from '../../services/DataHelperFunctions';
import { ProductApi } from '../../api/ProductApi';
import { getImages } from './ImagesSlice';
import { getSalesText } from './SalesTextSlice';
import {
  getProperties,
  getPropertyAssociations,
  getFeaturePropertyAssociations,
  getAllProperties,
  getEnumListOfValues,
} from './PropertySlice';
import { getPrice, getPriceListMap } from './PriceSlice';
import { getCountryList, getMarkets, getMarketAssociation } from './MarketSlice';
import { getLanguageAssociation, getLanguages } from './LanguageSlice';
import { compareProductHierarchy, getProductHierarchy } from './ProductHierarchySlice';
import { updateTableData } from '../../store/utilities/SliceDataHelper';

const initialState = {
  data: [],
  salesText: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  images: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  productHierarchy: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  compareProductHierarchy: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  prices: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  languageAssociation: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  languages: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  priceListMap: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  productCatalog: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  internalProductCatalog: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  productConfiguration: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  archivedConfiguration: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  markets: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  marketAssociation: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  countries: [],
  uploadFile: {
    file: {},
    importDropzone: null,
  },
  chunkUpload: {
    type: '',
    fileName: '',
    fileId: '',
    isUploaded: false,
    uploadValue: 0,
    isUploadProgress: false,
  },
  properties: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  propertyFields: {
    types: [],
    applicabilities: [],
  },
  propertyAssociation: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  featurePropertyAssociation: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  },
  propertyList: [],
  enumListOfValues: [],
};

//To get Product Catalog paginated data
export const getProductCatalog = createAsyncThunk(
  'dataMaintenance/getProductCatalog',
  async (action: { page: number; limit: number; searchCode?: string }, thunkAPI) => {
    return ProductApi.getProductCatalog(action, thunkAPI.dispatch);
  }
);

//To get Internal Product Catalog paginated data
export const getInternalProductCatalog = createAsyncThunk(
  'dataMaintenance/getInternalProductCatalog',
  async (action: { page: number; limit: number; searchCode?: string }, thunkAPI) => {
    return ProductApi.getInternalProductCatalog(action, thunkAPI.dispatch);
  }
);

//To get Product Configuration paginated data
export const getProductConfiguration = createAsyncThunk(
  'dataMaintenance/getProductConfiguration',
  async (action: { page: number; limit: number; searchCode?: string }, thunkAPI) => {
    return ProductApi.getProductConfiguration(action, thunkAPI.dispatch);
  }
);

//To get Deleted Configuration paginated data
export const getArchivedConfiguration = createAsyncThunk(
  'dataMaintenance/getArchivedConfiguration',
  async (action: { page: number; limit: number; searchCode?: string }, thunkAPI) => {
    return ProductApi.getArchivedConfiguration(action, thunkAPI.dispatch);
  }
);

const dataMaintenanceSlice = createSlice({
  name: 'dataMaintenance',
  initialState,
  reducers: {
    resetCompareProductHierarchy(state) {
      state.compareProductHierarchy = initialState.compareProductHierarchy;
    },
    setUploadFile(state, action) {
      state.uploadFile.file = action.payload.file;
      state.uploadFile.importDropzone = action.payload.importDropzone;
    },
    removeUploadFile(state) {
      state.uploadFile.file = initialState.uploadFile.file;
      state.uploadFile.importDropzone = initialState.uploadFile.importDropzone;
    },
    setChunkFileUpload(state, action) {
      state.chunkUpload = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSalesText.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.salesText, action.payload);
        }
        return state;
      })
      .addCase(getImages.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.images, action.payload);
        }
        return state;
      })
      .addCase(getProductHierarchy.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.productHierarchy, action.payload);
        }
        return state;
      })
      .addCase(compareProductHierarchy.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.compareProductHierarchy, action.payload);
        }
        return state;
      })
      .addCase(getPrice.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.prices, action.payload);
        }
        return state;
      })
      .addCase(getPriceListMap.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.priceListMap, action.payload);
        }
        return state;
      })
      .addCase(getCountryList.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          state.countries = action.payload.data;
        }
      })
      .addCase(getMarkets.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.markets, action.payload);
        }
        return state;
      })
      .addCase(getMarketAssociation.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.marketAssociation, action.payload);
        }
        return state;
      })
      .addCase(getLanguages.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.languages, action.payload);
        }
        return state;
      })
      .addCase(getLanguageAssociation.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.languageAssociation, action.payload);
        }
        return state;
      })
      .addCase(getProductCatalog.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.productCatalog, action.payload);
        }
        return state;
      })
      .addCase(getInternalProductCatalog.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.internalProductCatalog, action.payload);
        }
        return state;
      })
      .addCase(getProductConfiguration.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.productConfiguration, action.payload);
        }
        return state;
      })
      .addCase(getArchivedConfiguration.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(state.archivedConfiguration, action.payload);
        }
        return state;
      })
      .addCase(getProperties.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          action.payload.data = action.payload.data?.properties;
          return updateTableData(state.properties, action.payload);
        }
        return state;
      })
      .addCase(getPropertyAssociations.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          action.payload.data = getProductPropertyAssociationRows(action.payload.data);
          return updateTableData(state.propertyAssociation, action.payload);
        }
        return state;
      })
      .addCase(getFeaturePropertyAssociations.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          const errorAction = action.payload.error.action as unknown as {
            searchCode: string;
            page: number;
          };
          const payload = {
            data: [],
            searchKey: errorAction.searchCode,
            page: errorAction.page,
            totalRecords: 1,
          };
          return updateFeaturePropertyAssociation(state.featurePropertyAssociation, payload);
        } else {
          action.payload.data = getFeaturePropertyRows(
            action.payload.data ? action.payload.data : [],
            action.payload.searchKey ?? ''
          );
          return updateFeaturePropertyAssociation(state.featurePropertyAssociation, action.payload);
        }
      })
      .addCase(getAllProperties.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          state.propertyList = action.payload.data.properties;
        }
      })
      .addCase(getEnumListOfValues.fulfilled, (state, action) => {
        if (action.payload && !('error' in action.payload)) {
          state.enumListOfValues = action.payload.data;
        }
      });
  },
});

export const { resetCompareProductHierarchy, setUploadFile, removeUploadFile, setChunkFileUpload } =
  dataMaintenanceSlice.actions;

export default dataMaintenanceSlice.reducer;
